import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Spinner } from "react-bootstrap";
import cloudinarySized, { cloudinaryAttachment } from "./cloudinary_sized";

const sizedSrc = (image) => cloudinarySized(image.image_url, "c_limit,w_1600,h_1600");

/**
 * GalleryViewer — one gallery image at full size in a centred modal (the shape of messaging's
 * VideoPreviewModal), with previous/next buttons and the arrow keys to step through the rest.
 * Escape and the close button come from the Modal. Renders nothing while no image is open.
 * On phones the image takes the full width and the buttons sit below it.
 * The image shown stays up, faded under a spinner, until the next one has loaded; once it has,
 * the images either side are fetched ahead so stepping to them is instant.
 *
 * @param {Object} props
 * @param {string} props.title - The gallery's title ("Headshots", "Assets"), used in the header
 * @param {Array<{ id: number|string, image_url: string }>} props.images - The gallery's images
 * @param {number|null} props.index - The open image, or null when the viewer is closed
 * @param {Function} props.onIndexChange - Called with the image to show next
 * @param {Function} props.onClose - Called when the viewer is dismissed
 */
function GalleryViewer({ title, images, index = null, onIndexChange, onClose }) {
  const [originalUrls, setOriginalUrls] = useState(() => new Set());
  const viewerSrc = (img) => (originalUrls.has(img.image_url) ? img.image_url : sizedSrc(img));
  const count = images.length;
  const image = index === null ? null : images[index];
  const src = image ? viewerSrc(image) : null;
  const previousSrc = index > 0 ? viewerSrc(images[index - 1]) : null;
  const nextSrc = index !== null && index < count - 1 ? viewerSrc(images[index + 1]) : null;
  const [shown, setShown] = useState(null);
  const loading = shown?.src !== src;

  useEffect(() => {
    if (!loading) {
      [previousSrc, nextSrc].filter(Boolean).forEach((url) => {
        new Image().src = url;
      });
    }
  }, [loading, previousSrc, nextSrc]);

  useEffect(() => {
    if (index === null) {
      setShown(null);
      return undefined;
    }
    const step = (event) => {
      if (event.key === "ArrowLeft" && index > 0) {
        onIndexChange(index - 1);
      } else if (event.key === "ArrowRight" && index < count - 1) {
        onIndexChange(index + 1);
      }
    };
    globalThis.addEventListener("keydown", step);
    return () => globalThis.removeEventListener("keydown", step);
  }, [index, count, onIndexChange]);

  if (!image) {
    return null;
  }

  return (
    <Modal show onHide={onClose} centered size="lg" className="gallery-viewer">
      <Modal.Header closeButton className="border-0">
        <Modal.Title>{`${title} (${index + 1} of ${count})`}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-wrap flex-sm-nowrap align-items-center justify-content-center gap-3">
        {count > 1 && (
          <button
            type="button"
            className={`btn btn-secondary gallery-step${index === 0 ? " disabled" : ""}`}
            aria-label="Previous image"
            aria-disabled={index === 0 || undefined}
            onClick={() => {
              if (index > 0) {
                onIndexChange(index - 1);
              }
            }}
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
          </button>
        )}
        <div
          className="gallery-viewer-stage position-relative d-flex align-items-center justify-content-center flex-grow-1 order-first order-sm-0 w-100 min-w-0"
          aria-busy={loading}
        >
          {shown && (
            <div className="gallery-viewer-frame position-relative">
              <img className="gallery-viewer-image rounded" src={shown.src} alt={shown.alt} />
              <div className="gallery-viewer-actions position-absolute top-0 end-0 d-flex">
                <a
                  className="gallery-viewer-action"
                  href={cloudinaryAttachment(shown.original)}
                  download
                  aria-label="Download image"
                  title="Download"
                >
                  <i className="fa-solid fa-download" aria-hidden="true" />
                </a>
                <a
                  className="gallery-viewer-action"
                  href={shown.original}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open image in new tab"
                  title="Open in New Tab"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                </a>
              </div>
            </div>
          )}
          {loading && (
            <>
              <img
                key={src}
                src={src}
                alt=""
                hidden
                onLoad={() => setShown({ src, alt: `${title} ${index + 1} of ${count}`, original: image.image_url })}
                onError={() =>
                  src === image.image_url
                    ? setShown({ src, alt: `${title} ${index + 1} of ${count}`, original: image.image_url })
                    : setOriginalUrls((prev) => new Set(prev).add(image.image_url))
                }
              />
              <div className="gallery-viewer-spinner position-absolute">
                <Spinner animation="border" variant="secondary" role="status">
                  <span className="visually-hidden">Loading image</span>
                </Spinner>
              </div>
            </>
          )}
        </div>
        {count > 1 && (
          <button
            type="button"
            className={`btn btn-secondary gallery-step${index === count - 1 ? " disabled" : ""}`}
            aria-label="Next image"
            aria-disabled={index === count - 1 || undefined}
            onClick={() => {
              if (index < count - 1) {
                onIndexChange(index + 1);
              }
            }}
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </button>
        )}
      </Modal.Body>
    </Modal>
  );
}

GalleryViewer.propTypes = {
  title: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      image_url: PropTypes.string,
    }),
  ).isRequired,
  index: PropTypes.number,
  onIndexChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GalleryViewer;
